/**
 * Pure GamePage helpers — extracted from GamePage.jsx for readability.
 *
 * These are state-derivation functions only. No side effects, no React.
 * Keeping them here lets the main page component focus on orchestration
 * (socket lifecycle, bot-turn staging, action dispatch) while still
 * sharing the same parsing/heuristic logic with any future view that
 * needs to describe recent bot activity or suggest a strategy hint.
 */

import { CRYPTO_WIN_THRESHOLD as WIN } from "./constants";

/**
 * Parse new event_log entries emitted by a single `runBotTurn` response
 * into a per-bot list of first-decisions. Only the FIRST decision per
 * bot is returned (used to show the "chose: BUY BTC" banner).
 *
 * @param {Array} newEntries - event_log slice produced since prev turn
 * @param {Array<string>} botNames - names of active bot players
 * @returns {Array<{botName, verb, symbol?, type}>}
 */
export function extractBotTurns(newEntries, botNames) {
  const turns = [];
  const seen = new Set();
  for (const ev of newEntries || []) {
    const msg = ev?.message || "";
    const botName = botNames.find((n) => msg.startsWith(n));
    if (!botName || seen.has(botName)) continue;
    let action = null;
    if (ev.type === "buy") {
      const m = msg.match(/bought (\w+)/);
      action = { verb: "BUY", symbol: m?.[1], type: "buy" };
    } else if (ev.type === "buy_fail") {
      const m = msg.match(/drew (\w+)/);
      action = { verb: "DREW", symbol: m?.[1], type: "buy_fail" };
    } else if (ev.type === "sell") {
      const m = msg.match(/sold (\w+)/);
      action = { verb: "SELL", symbol: m?.[1], type: "sell" };
    } else if (ev.type === "hold") {
      action = { verb: "HOLD", type: "hold" };
    }
    if (action) {
      turns.push({ botName, ...action });
      seen.add(botName);
    }
  }
  return turns;
}

/**
 * Produce a short human-readable summary of each bot's most recent
 * decision SINCE the given human player last acted. Used by the
 * Ready overlay's "While you waited" chip.
 *
 * Scans event_log backwards from the latest entry until it hits an
 * entry starting with `humanName` (the human's last action).
 *
 * @param {object} game - server game state
 * @param {string} humanName - name of the human whose turn just started
 * @returns {Array<{name: string, text: string, type: string}>}
 */
export function getRecentAISummary(game, humanName) {
  const log = game?.event_log || [];
  const summary = [];
  const seen = new Set();
  for (let i = log.length - 1; i >= 0; i--) {
    const ev = log[i];
    const msg = ev?.message || "";
    if (humanName && msg.startsWith(humanName + " ")) break;
    let match = null;
    if (ev.type === "buy") {
      const m = msg.match(/^(.+?) bought (\w+)/);
      if (m) match = { name: m[1], text: `bought ${m[2]}`, type: "buy" };
    } else if (ev.type === "sell") {
      const m = msg.match(/^(.+?) sold (\w+)/);
      if (m) match = { name: m[1], text: `sold ${m[2]}`, type: "sell" };
    } else if (ev.type === "hold") {
      const m = msg.match(/^(.+?) holds/);
      if (m) match = { name: m[1], text: "held", type: "hold" };
    } else if (ev.type === "buy_fail") {
      const m = msg.match(/^(.+?) drew (\w+)/);
      if (m) match = { name: m[1], text: `drew ${m[2]} (no buy)`, type: "buy_fail" };
    }
    if (match && !seen.has(match.name)) {
      seen.add(match.name);
      summary.unshift(match);
    }
  }
  return summary;
}

/**
 * Produce a 1-line context-aware strategy hint for the Ready overlay.
 * Purely derived from game state — no RNG, no LLM — so it feels fast
 * and deterministic.
 *
 * Tones map to an accent color:
 *   "green" = good news, "red" = threat,
 *   "amber" = important window, "blue" = neutral/informational.
 *
 * @returns {{text: string, tone: "green"|"red"|"amber"|"blue"} | null}
 */
export function getStrategyHint(game, playerIdx) {
  if (!game || playerIdx == null || !game.players?.[playerIdx]) return null;
  const me = game.players[playerIdx];
  const opponents = game.players.filter((_, i) => i !== playerIdx);
  const myValue = me.crypto_value || 0;
  const topOppValue = Math.max(0, ...opponents.map((p) => p.crypto_value || 0));

  const closestOpp = opponents.find((p) => (p.crypto_value || 0) >= WIN - 200);
  if (closestOpp) {
    return {
      text: `${closestOpp.name} is close to $${WIN.toLocaleString()} — disrupt with an effect card, don't let them coast.`,
      tone: "red",
    };
  }
  if (myValue >= WIN - 200) {
    return {
      text: `You're $${WIN - myValue} from winning — hold onto high-value cards and avoid risky plays.`,
      tone: "green",
    };
  }
  if ((game.market_melee_count || 0) >= 2) {
    return {
      text: "One more Market Melee ends the game — push your crypto value every turn.",
      tone: "amber",
    };
  }
  if (topOppValue > 0 && myValue < topOppValue - 300) {
    return {
      text: "You're trailing badly — get aggressive: buy, pump, and chain effects.",
      tone: "amber",
    };
  }
  if (topOppValue > 0 && myValue < topOppValue - 150) {
    return {
      text: `Trailing by $${topOppValue - myValue} — buying new crypto beats holding this turn.`,
      tone: "blue",
    };
  }
  if (!me.portfolio || me.portfolio.length === 0) {
    return {
      text: "No crypto yet — buying should be your top priority this turn.",
      tone: "blue",
    };
  }
  const hasHoldable = (me.hand || []).some((c) => !c.persistent_symbol);
  if (hasHoldable) {
    return {
      text: "You have a held card — tap it to see when it's best played.",
      tone: "blue",
    };
  }
  if (myValue > topOppValue + 150) {
    return {
      text: `You lead by $${myValue - topOppValue} — stay patient, protect your highest crypto.`,
      tone: "green",
    };
  }
  return {
    text: "Close race — every action counts. Pick what moves you closer to $1,250.",
    tone: "blue",
  };
}
